import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { DetailList, DetailRow } from '@/shared/ui/detail-list';

import { bytesParts } from '../api/mappers';
import { useOrganizationUsage } from '../hooks/use-organization-usage';

export interface UsageCardProps {
  organizationId: string;
  className?: string;
}

/** What the tenant holds today: catalog, orders, stores by status, people, keys and media. */
export function UsageCard({ organizationId, className }: UsageCardProps) {
  const { t } = useTranslation('organizations');
  const format = useFormat();
  const usage = useOrganizationUsage(organizationId);
  const data = usage.data;

  return (
    <Card className={className} data-tour="platform.organizations.usage">
      <CardHeader>
        <CardTitle>{t('usage.title')}</CardTitle>
        <CardDescription>{t('usage.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {usage.isError ? (
          <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />
        ) : !data ? (
          <div className="space-y-3" aria-busy>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
          </div>
        ) : (
          <DetailList aria-label={t('usage.title')}>
            <DetailRow label={t('usage.products')}>
              {t('usage.productsValue', {
                products: format.number(data.products),
                variants: format.number(data.variants),
              })}
            </DetailRow>
            <DetailRow label={t('usage.orders')}>
              {t('usage.ordersValue', {
                recent: format.number(data.orders.last30Days),
                total: format.number(data.orders.total),
              })}
            </DetailRow>
            <DetailRow label={t('usage.stores')}>
              {t('usage.storesValue', {
                active: data.stores.active,
                draft: data.stores.draft,
                paused: data.stores.paused,
                closed: data.stores.closed,
              })}
            </DetailRow>
            <DetailRow label={t('usage.members')}>{format.number(data.members)}</DetailRow>
            <DetailRow label={t('usage.apiKeys')}>{format.number(data.apiKeys)}</DetailRow>
            <DetailRow label={t('usage.media')}>
              <MediaBytes bytes={data.mediaBytes} />
            </DetailRow>
          </DetailList>
        )}
      </CardContent>
    </Card>
  );
}

function MediaBytes({ bytes }: { bytes: number }) {
  const format = useFormat();
  const { value, unit } = bytesParts(bytes);
  return (
    <span title={format.number(bytes)}>
      {format.number(value, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        maximumFractionDigits: 1,
      })}
    </span>
  );
}
